// This file is used to hold form filling state and configuration
import {
	useFieldValueProvider,
	type FieldValueResult,
} from '@/components/documents/Foxit/FormMapping/composables/FieldValueProvider'
import type {
	ServiceLineItem,
	MerchandiseLineItem,
	CashAdvanceLineItem,
} from '@/components/documents/Foxit/FormMapping/formMappingTypes'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { usePresentationLocale } from '@/i18n'
import type {
	CartItemAndLineItem,
	CartSubItemAndLineItem,
} from '@/mixins/merchandise'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { CustomCartItem } from '@/types'

/**
 * Normalized line item for form filling. All kinds (Service, Merchandise, CashAdvance)
 * are stored in one array.
 */
export type MappedCartItem = {
	name: string
	category: PbProductType | ''
	subcategory: PbSubcategory | string
	isBundle: boolean
	isGuaranteed: boolean
	price: number | string
	isIncludedInPkg: boolean
	isSoftDeleted: boolean
	isVirtualLine: boolean
	shortDescription?: string
} & Record<string, string | number | boolean | undefined>

export type MappedCartSubItem = CartSubItemAndLineItem & {
	isIncludedInPkg: boolean
}

export type SectionOverride = {
	field: string
	section: string
}

export type CategoryOverride = {
	field: string
	categories: PbProductType[]
}
export enum MultipleItemsMethod {
	Supplemental = 'supplemental',
	CommaSeparated = 'commaSeparated',
}

let cartItems: Map<string, MappedCartItem[]> = new Map()
let multipleItemsMethod: MultipleItemsMethod = MultipleItemsMethod.Supplemental
let fieldNameProvider: any | undefined = undefined
let softDeletedServiceSubItems: MappedCartItem[] = []
let nonServiceCategories: PbProductType[] = []
let otherLineCounts: Record<string, number> = {}
let sectionOverrides: SectionOverride[] = []
let categoryOverrides: CategoryOverride[] = []

// Cart items the field providers read while tracking, used to find which fields a custom item fills
let itemsRead: MappedCartItem[] | undefined

// Some fields should not show the text 'See Supplemental Page'.
// This is necessary for some states (e.g. Wisconsin with multiple fields on the same line).
let ignoreSupplementalTextList: Set<string> = new Set()

export const useFormFillHelper = () => {
	const sessionStore = useSessionStore()
	const sessionDocumentsStore = useSessionDocumentsStore()
	const { t } = usePresentationLocale()

	// Forms have different ways of handling multiple items, this gets set per form prior to form filling
	const setMultipleItemsMethod = (method: MultipleItemsMethod) => {
		multipleItemsMethod = method
	}

	const setFieldNameProvider = (provider: any) => {
		fieldNameProvider = provider
	}

	const setIgnoreSupplementalTextList = (list: Set<string>) => {
		ignoreSupplementalTextList = list
	}

	const setOtherLineCounts = (counts: Record<string, number>) => {
		otherLineCounts = counts
	}

	const setNonServiceCategories = (
		categories: PbProductType[] = [
			PbProductType.Merchandise,
			PbProductType.CashAdvance,
			PbProductType.FamilyTravel,
			PbProductType.Cemetery,
			PbProductType.Afterward,
			PbProductType.Other,
		]
	) => {
		nonServiceCategories = categories
	}

	const recordItemsRead = <
		T extends MappedCartItem | MappedCartItem[] | undefined,
	>(
		items: T
	): T => {
		if (itemsRead && items) {
			itemsRead.push(...[items].flat())
		}

		return items
	}

	// Runs the callback and returns the cart items it read through the find helpers
	const trackItemsRead = <T>(callback: () => T) => {
		const tracked: MappedCartItem[] = []
		itemsRead = tracked

		try {
			return { result: callback(), itemsRead: tracked }
		} finally {
			itemsRead = undefined
		}
	}

	// Helper to add item to map (skipping softDeleted unless it's a softDeleted child for tracking)
	const addToCartItemMap = (item: Partial<MappedCartItem>) => {
		if (item.isSoftDeleted) {
			return
		}

		const key = `${item.category}-${item.subcategory}`

		if (!cartItems.has(key)) {
			cartItems.set(key, [])
		}

		cartItems.get(key)!.push(item as MappedCartItem)
	}

	const handlePackageSubItems = (items: CartItemAndLineItem[]) => {
		const packageItem = items.find(
			(item) => item.lineItem?.category === PbProductType.Package
		)
		// Clear the soft deleted service sub items array
		softDeletedServiceSubItems = []

		// If the package is soft deleted, there are no sub items to return
		if (packageItem?.isSoftDeleted) {
			return []
		}

		// Grab the sub items from the package
		// Separate soft deleted service sub items so we can subtract them from the package price
		const subItems: MappedCartSubItem[] = []
		packageItem?.subItems.forEach((subItem: CartSubItemAndLineItem) => {
			if (
				subItem.isSoftDeleted &&
				subItem.lineItem?.category === PbProductType.Service
			) {
				softDeletedServiceSubItems.push(
					normalizeCartItem(subItem as CartSubItemAndLineItem)
				)
			} else {
				subItems.push({
					...subItem,
					isIncludedInPkg: true,
				})
			}
		})

		return subItems
	}

	const handleEssentialsBundleSubItems = (items: CartItemAndLineItem[]) => {
		const essentialsBundle = items.find(
			(item) =>
				item.lineItem?.category === PbProductType.Package &&
				item.lineItem?.subcategory === PbSubcategory.PackageEssentials
		)

		if (!essentialsBundle || essentialsBundle?.isSoftDeleted) {
			return []
		}

		return essentialsBundle.subItems as MappedCartSubItem[]
	}

	const handleRegularCartItems = (
		regularCartItems: (CartItemAndLineItem | MappedCartSubItem)[]
	) => {
		regularCartItems.forEach(
			(
				item:
					| (CartItemAndLineItem & { isIncludedInPkg?: boolean })
					| MappedCartSubItem
			) => {
				if (item.isSoftDeleted || item.lineItem?.isVirtualLine) {
					return
				}

				const normalizedItem = normalizeCartItem(item as CartSubItemAndLineItem)

				addToCartItemMap(normalizedItem)
			}
		)
	}

	// Custom Cart Item prices are set by the AFP and should not be multiplied by the quantity.
	const handleCustomCartItems = (customCartItems: CustomCartItem[]) => {
		customCartItems
			.filter((item) => !item.isSoftDeleted)
			.forEach((customItem) =>
				addToCartItemMap({
					...customItem,
					name: customItem.title ?? '',
					category: customItem.category ?? '',
					subcategory: customItem.subcategory ?? '',
					price: customItem.price ?? '',
					shortDescription: customItem.shortDescription ?? '',
					isIncludedInPkg: false,
					isSoftDeleted: customItem.isSoftDeleted,
				} as Partial<MappedCartItem>)
			)
	}

	// We set and store the cart items for all form fill composables to refer to
	// We normalize the cart items so they all have the same structure for easier processing
	const setCartItems = (items: CartItemAndLineItem[]) => {
		// Clear the cart items map so we don't have duplicate items
		cartItems.clear()

		const planHolder = sessionStore.getPlanHolder(
			sessionDocumentsStore.currentPlanHolderID
		)!
		const packageSubItems = handlePackageSubItems(items)
		const essentialsBundleSubItems = handleEssentialsBundleSubItems(items)

		handleRegularCartItems([
			...items,
			...packageSubItems,
			...essentialsBundleSubItems,
		])
		handleCustomCartItems(planHolder?.customCartItems)
	}

	const normalizeCartItem = (
		item: CartSubItemAndLineItem & { isIncludedInPkg?: boolean }
	): MappedCartItem => {
		const line = item.lineItem as ServiceLineItem &
			MerchandiseLineItem &
			CashAdvanceLineItem

		return {
			name: line.name,
			isBundle: line.isBundle ?? false,
			category: line.category ?? '',
			subcategory: line.subcategory ?? '',
			isGuaranteed: line.isGuaranteed,
			price:
				item.price ?? ((line.fixedPrice as number) * (item.quantity ?? 1) || 0),
			mileRadiusForStandardService: line.mileRadiusForStandardService ?? '',
			amountPerMile: line.amountPerMile ?? '',
			quantity: item?.quantity ?? 1,
			numberOfDays: line.numberOfDays ?? '',
			shortDescription:
				line.shortDescription ??
				(line.subcategory === PbSubcategory.Discount ? line.name : ''),
			interiorDescription: line.interiorDescription ?? '',
			exteriorDescription: line.exteriorDescription ?? '',
			modelNameNumber: line.modelNameNumber ?? '',
			productManufacturer: line.productManufacturer ?? '',
			amountPerDeathCertificate: line.amountPerDeathCertificate ?? 0,
			isIncludedInPkg: item?.isIncludedInPkg ?? false,
			isSoftDeleted: item.isSoftDeleted ?? false,
			isVirtualLine: item.lineItem?.isVirtualLine ?? false,
		}
	}

	// Some cart items only need to be found by category
	const findCartItemByCategory = (category: PbProductType) => {
		const itemsArray = [...cartItems.values()].flat()
		return recordItemsRead(
			itemsArray.find((item) => item.category === category)
		)
	}

	const findCartItemsByCategory = (category: PbProductType) => {
		const itemsArray = [...cartItems.values()].flat()
		return recordItemsRead(
			itemsArray.filter((item) => item.category === category)
		)
	}

	const findCartItemsByCategories = (
		categories: PbProductType[]
	): MappedCartItem[] | [] => {
		const itemsArray: MappedCartItem[] = []

		categories.forEach((category) => {
			const items = findCartItemsByCategory(category)
			itemsArray.push(...items)
		})

		return itemsArray
	}

	const findCartItemsInMultipleSubcategories = (
		category: PbProductType,
		subcategories: PbSubcategory[]
	): MappedCartItem[] | [] => {
		const itemsArray: MappedCartItem[] = []

		subcategories.forEach((subcategory) => {
			const key = `${category}-${subcategory}`
			const items = cartItems.get(key) ?? []
			itemsArray.push(...items)
		})

		return recordItemsRead(itemsArray)
	}

	// Grab package item but not essentials bundle items
	const findPackageItem = () => {
		const itemsArray = [...cartItems.values()].flat()
		return recordItemsRead(
			itemsArray.find(
				(item) =>
					item.category === PbProductType.Package &&
					item.subcategory !== PbSubcategory.PackageEssentials
			)
		)
	}

	const findCartItemsInMultipleCategories = (
		categories: PbProductType[],
		subcategory: PbSubcategory
	): MappedCartItem[] | [] => {
		const itemsArray: MappedCartItem[] = []

		categories.forEach((category) => {
			const key = `${category}-${subcategory}`
			const items = cartItems.get(key) ?? []
			itemsArray.push(...items)
		})

		return recordItemsRead(itemsArray)
	}

	// Most cart items need to be found by category and subcategory
	// They may have multiple items across cart items, sub items and custom cart items
	const findCartItems = (
		category: PbProductType,
		subcategory: PbSubcategory
	): MappedCartItem[] | [] => {
		const key = `${category}-${subcategory}`

		return recordItemsRead(cartItems.get(key) ?? [])
	}

	const getMultipleItemsText = (
		items: MappedCartItem[],
		property: string = 'shortDescription'
	) => {
		if (multipleItemsMethod === MultipleItemsMethod.Supplemental) {
			return 'See Supplemental Page'
		}

		// There are states without supplemental pages (e.g. Texas) where the description of multiple items are shown on the same line.
		return items
			.map((item) => item?.[property as keyof MappedCartItem])
			.join(', ')
	}

	const getFieldPrice = (items: MappedCartItem[]) => {
		if (items.length === 0) {
			return undefined
		}

		if (items.length > 1) {
			return addPrices(items)
		}

		return getItemPrice(items[0])
	}

	const addPrices = (
		items: MappedCartItem[],
		useIncludedLogic: boolean = true
	): string | number => {
		const total =
			items.reduce((acc, item) => {
				const price = useIncludedLogic
					? getItemPrice(item)
					: ((item.price as number) ?? 0)

				return acc + (price === 'Incl' ? 0 : (price as number))
			}, 0) ?? 0

		return total === 0 && useIncludedLogic ? 'Incl' : total
	}

	const getItemPrice = (item: MappedCartItem) => {
		return sessionStore.activePriceBookVersion === 1
			? v1pricingLogic(item)
			: v2pricingLogic(item)
	}

	// Package sub items are included in the package price and should be shown as 'Incl' if the following is true:
	// - when the category is Service
	// - when the category is Transportation
	// - when both the category is Merchandise AND the price is 0
	const v1pricingLogic = (item: MappedCartItem): number | 'Incl' => {
		if (
			item?.isIncludedInPkg &&
			(item.category === PbProductType.Service ||
				item.category === PbProductType.Transportation ||
				(item.category === PbProductType.Merchandise && item.price === 0))
		) {
			return 'Incl'
		}

		return (item?.price ?? 0) as number
	}

	// For v2 price books, the price is shown as 'Incl' if the price is 0
	const v2pricingLogic = (item: MappedCartItem): number | 'Incl' => {
		return Number(item?.price) === 0 ? 'Incl' : Number(item?.price)
	}

	const getFieldDescription = (items: MappedCartItem[]) => {
		if (items.length === 0) {
			return undefined
		}

		return items.length > 1
			? getMultipleItemsText(items)
			: (items[0]?.shortDescription as string | undefined)
	}

	const getAttributeValue = (items: MappedCartItem[], attribute: string) => {
		if (items.length === 0) {
			return undefined
		}

		const value = items[0]?.[attribute as keyof MappedCartItem]

		if (typeof value === 'boolean') {
			return value ? 'Yes' : 'Off'
		}

		return items.length > 1
			? getMultipleItemsText(items, attribute)
			: (items[0]?.[attribute as keyof MappedCartItem] as
					| string
					| number
					| undefined)
	}

	const getCheckBoxFieldValueFor = (
		category: PbProductType,
		fieldNames: Record<string, string>
	) => {
		const checkBoxFields: {
			name: string
			value: string
			format: boolean
			section: string
		}[] = []

		Object.entries(fieldNames).forEach(([subcategory, fieldName]) => {
			const items = findCartItems(category, subcategory as PbSubcategory)

			checkBoxFields.push({
				name: fieldName,
				value: items.length > 0 ? 'Yes' : 'Off',
				format: false,
				section: 'header',
			})
		})

		return checkBoxFields
	}

	const formatFields = (fields: FieldValueResult[]): FieldValueResult[] => {
		return fields.map((field) => ({
			...field,
			value:
				field.format && typeof field.value === 'number'
					? field.value.toFixed(2)
					: field.value,
		}))
	}

	// Helper to get a localized title for sections that goes into Supplemental page.
	// If the category is 'Service-Other', simply return translated 'Other'
	const getLocalizedSubcategoryTitle = (subcategory: string): string => {
		const categories = subcategory.split('-')

		if (categories.length > 1) {
			return t(`productCategories.${categories[1]}`)
		}

		return t(`productSubcategories.${subcategory}`)
	}

	const findIncludedInPkgItemsByCategories = (categories: PbProductType[]) => {
		const itemsArray = [...cartItems.values()].flat()
		return recordItemsRead(
			itemsArray.filter(
				(item) =>
					item.isIncludedInPkg &&
					categories.includes(item.category as PbProductType)
			)
		)
	}

	const mapFields = (fields: string[]) => {
		const { fieldToValue } = useFieldValueProvider()

		return fields
			.map((field) => {
				let results: FieldValueResult[] = []
				try {
					results = fieldToValue(
						field,
						fieldNameProvider[field] as string & Record<string, string>
					)
				} catch (error) {
					console.error(
						`Error mapping field ${field} with PDF field name ${fieldNameProvider[field]}`
					)
				}

				const fields: FieldValueResult[] = []
				results.forEach((result) => {
					if (result.value !== undefined) {
						if (
							result.value === 'See Supplemental Page' &&
							ignoreSupplementalTextList.has(result.name)
						) {
							result.value = ''
						}

						fields.push(result)
					}
				})

				return fields
			})
			.flat()
	}

	const singlePayCCIsAllowed = () => {
		return sessionStore.activeFh?.isAllowedToSellSinglePayCc ?? false
	}

	const calculateSectionTotals = () => {
		const totals = {
			guaranteedFuneralSubtotal: 0,
			nonGuaranteedFuneralSubTotal: 0,
		}

		for (const items of cartItems.values()) {
			items.forEach((item: MappedCartItem) => {
				if (
					item.isVirtualLine ||
					item.isSoftDeleted ||
					item.category === PbProductType.Package
				) {
					return
				}

				if (
					item.category === PbProductType.CashAdvance ||
					item.category === PbProductType.Other
				) {
					totals.nonGuaranteedFuneralSubTotal += item.price as number
				} else {
					totals.guaranteedFuneralSubtotal += item.price as number
				}
			})
		}
		return totals
	}

	const multilineWithSingleTotalFields = (
		items: MappedCartItem[],
		fieldNames: Record<string, string>,
		section: string,
		descriptionSuffix: string = 'Desc',
		priceSuffix: string = 'Amt',
		useSupplemental: boolean = true
	) => {
		const fieldValues: FieldValueResult[] = []
		const descFieldNames = filterFieldNamesByString(
			fieldNames,
			descriptionSuffix
		)
		const overflow = items.length > descFieldNames.length

		descFieldNames.forEach((fieldName, index) => {
			const isFirstAndOverflow = overflow && index === 0
			const shouldBeEmpty = !items[index] || (overflow && index !== 0)

			fieldValues.push({
				name: `${fieldName}`,
				value: isFirstAndOverflow
					? getFieldDescription(items)
					: shouldBeEmpty
						? ''
						: getFieldDescription([items[index]]),
				format: false,
				section: section,
				supplementalItems:
					isFirstAndOverflow && useSupplemental ? items : undefined,
			})
		})

		const totalPrice = getFieldPrice(items)
		fieldValues.push({
			name: findFieldNameByString(fieldNames, priceSuffix),
			value: totalPrice === 0 ? 'Incl' : totalPrice,
			format: true,
			section: section,
		})

		return fieldValues
	}

	const findFieldNameByString = (
		fieldNames: Record<string, string>,
		needle: string
	) => {
		return (
			Object.values(fieldNames).find((fieldName) =>
				fieldName.includes(needle)
			) ?? ''
		)
	}

	const filterFieldNamesByString = (
		fieldNames: Record<string, string>,
		needle: string
	) => {
		return Object.values(fieldNames).filter((fieldName) =>
			fieldName.includes(needle)
		)
	}

	const multilineOtherFields = (
		fieldNames: Record<string, string>,
		items: MappedCartItem[],
		section: string,
		totalFieldName?: string
	): FieldValueResult[] => {
		const fieldValues: FieldValueResult[] = []

		const fieldNameValues = Object.values(fieldNames)
		for (let index = 0; index < fieldNameValues.length - 1; index++) {
			if (!items[index]) {
				continue
			}

			const fieldName = fieldNameValues[index]
			fieldValues.push({
				name: `${fieldName}Desc`,
				value: getFieldDescription([items[index]]),
				format: false,
				section,
			})

			fieldValues.push({
				name: `${fieldName}Amt`,
				value: getFieldPrice([items[index]]),
				format: true,
				section,
			})
		}

		if (items.length >= fieldNameValues.length) {
			const leftOverItems = items.slice(fieldNameValues.length - 1)
			fieldValues.push({
				name: `${fieldNameValues[fieldNameValues.length - 1]}Desc`,
				value: getFieldDescription(leftOverItems),
				format: false,
				section,
			})
			fieldValues.push({
				name: `${fieldNameValues[fieldNameValues.length - 1]}Amt`,
				value: getFieldPrice(leftOverItems),
				format: true,
				section,
				supplementalItems: leftOverItems,
			})
		}

		// The lines' own total is in a section no form totals, so it isn't counted again in the section total
		if (totalFieldName) {
			fieldValues.push({
				name: totalFieldName,
				value: getFieldPrice(items),
				format: true,
				section: 'otherLinesTotal',
				isTotal: true,
			})
		}

		return fieldValues
	}

	const generateOtherLineFieldNames = (fieldNames: string, count: number) => {
		return Object.fromEntries(
			Array.from({ length: count }, (_, index) => [
				`otherLine${index + 1}`,
				`${fieldNames}${index + 1}`,
			])
		)
	}

	// This is used to override the section for a field.
	// This is necessary for some states (e.g. Utah) where Acknowledgement Cards are shown on the Service section instead of the Merchandise section.
	const setSectionOverrides = (overrides: SectionOverride[]) => {
		sectionOverrides = overrides
	}

	const setCategoryOverrides = (overrides: CategoryOverride[]) => {
		categoryOverrides = overrides
	}

	return {
		cartItems,
		softDeletedServiceSubItems,
		multipleItemsMethod,
		nonServiceCategories,
		fieldNameProvider,
		otherLineCounts,
		sectionOverrides,
		categoryOverrides,
		setOtherLineCounts,
		setFieldNameProvider,
		setMultipleItemsMethod,
		setIgnoreSupplementalTextList,
		setCartItems,
		setNonServiceCategories,
		findIncludedInPkgItemsByCategories,
		findCartItemByCategory,
		findCartItemsByCategory,
		findCartItemsByCategories,
		findPackageItem,
		findCartItemsInMultipleSubcategories,
		findCartItemsInMultipleCategories,
		findCartItems,
		getMultipleItemsText,
		getFieldPrice,
		getItemPrice,
		getFieldDescription,
		getCheckBoxFieldValueFor,
		getLocalizedSubcategoryTitle,
		formatFields,
		addPrices,
		getAttributeValue,
		mapFields,
		trackItemsRead,
		singlePayCCIsAllowed,
		calculateSectionTotals,
		multilineWithSingleTotalFields,
		findFieldNameByString,
		filterFieldNamesByString,
		multilineOtherFields,
		generateOtherLineFieldNames,
		setSectionOverrides,
		setCategoryOverrides,
	}
}
